import React, { useEffect, FC, useState } from 'react';
import { checkUser } from '../services/authentication';
import { AuthContext } from '../context/AuthContext';
import { getMyProfile } from '@/services/profile';
import { handleInvalidData } from '@/utils/validate';
import { getUserInitialsFromName } from '@/utils/user';

interface Props {
  children: JSX.Element;
  user: any;
  setUser: (user: AuthContextStore) => void;
}

export const AuthProvider: FC<Props> = ({ children, user, setUser }) => {
  const [userInitials, setUserInitials] = useState<string>('');
  const [profileInfo, setProfileInfo] = useState<ProfileInfo>();
  const [isProfileFetched, setIsProfileFetched] = useState<boolean>(false);
  const [userId, setUserId] = useState('');

  const getUserData = async () => {
    const getUser = await checkUser();
    if (getUser) {
      setUser(getUser);
      setUserId(getUser?.signInUserSession?.idToken?.payload?.sub);
      if (!isProfileFetched) {
        fetchUserProfile();
      }
    }
  };

  useEffect(() => {
    getUserData();
    const interval = setInterval(() => {
      getUserData();
    }, 600000); //refresh session every 10 min , 1800000
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line
  }, []);

  const fetchUserProfile = async () => {
    let response;
    try {
      response = await getMyProfile();
    } catch (err) {
      console.error('error fetching user profile', err);
    }
    if (response && response[0]) {
      const profileInfo = response[0].doc;
      setProfileInfo({
        fullName: handleInvalidData(profileInfo.full_name),
        email: profileInfo.email,
        groupEmail: profileInfo.group_email,
        employee_number: response[0].id,
        optOut: profileInfo.opted_out,
        photo: handleInvalidData(profileInfo.photo),
        about: handleInvalidData(profileInfo.about),
        linkedin: handleInvalidData(profileInfo.linkedin),
        skills: profileInfo.skills,
        interests: profileInfo.topics_of_interest,
        isManager: profileInfo.is_manager,
        isMentor: profileInfo.is_mentor,
        mentorSkills: profileInfo.mentor_skills,
        profileDivision: profileInfo?.division,
        profileJobFunction: profileInfo?.job_function,
        profileJobTitle: profileInfo?.job_title,
        profileJobType: profileInfo?.job_type,
        profileLocation: profileInfo?.location,
      });
    }
    setUserInitials?.(getUserInitialsFromName(handleInvalidData(response[0].doc.full_name)));
    setIsProfileFetched(true);
  };

  const authContext: AuthContextStore = {
    ...user,
    userInitials,
    setUserInitials,
    profileInfo,
    userId,
    setProfileInfo,
  };

  return <AuthContext.Provider value={authContext}>{user ? children : ''}</AuthContext.Provider>;
};
